import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import AuthService from '../../services/authService';
import Goals from './Goals';

const Profile = () => {
  const { user, token, login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    profile: {
      age: '',
      height: '',
      weight: '',
      goals: ''
    }
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        profile: {
          age: user.profile?.age || '',
          height: user.profile?.height || '',
          weight: user.profile?.weight || '',
          goals: user.profile?.goals || ''
        }
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        [name]: value
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    
    // Validate password if changing
    if (formData.newPassword) {
      if (!formData.currentPassword) {
        toast.error('Current password is required to set a new password');
        return;
      }
      
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }
      
      if (formData.newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }
    
    try {
      setLoading(true);
      
      // Prepare data for API
      const updateData = {
        name: formData.name,
        profile: formData.profile
      };
      
      // Add password fields if changing password
      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }
      
      // Update profile
      const data = await AuthService.updateUserProfile(token, updateData);
      
      if (data.success) {
        toast.success('Profile updated successfully');
        
        // Update user context with new data
        if (data.user) {
          login(data.user, token);
        }
        
        // Clear password fields
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div className="profile">
      <h1>Your Profile</h1>
      
      <div className="profile-card">
        <form onSubmit={handleSubmit}>
          <h2>Account Information</h2>
          
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              disabled
              className="disabled-input"
            />
            <small>Email cannot be changed</small>
          </div>
          
          <h2>Personal Information</h2>
          
          <div className="form-group">
            <label htmlFor="age">Age</label>
            <input
              type="number"
              id="age"
              name="age"
              value={formData.profile.age}
              onChange={handleProfileChange}
              min="1"
              max="120"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="height">Height (cm)</label>
            <input
              type="number"
              id="height"
              name="height"
              value={formData.profile.height}
              onChange={handleProfileChange}
              min="1"
              max="300"
              step="0.1"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="weight">Weight (kg)</label>
            <input
              type="number"
              id="weight"
              name="weight"
              value={formData.profile.weight}
              onChange={handleProfileChange}
              min="1"
              max="500"
              step="0.1"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="goals">Fitness Goals</label>
            <textarea
              id="goals"
              name="goals"
              value={formData.profile.goals}
              onChange={handleProfileChange}
              rows="4"
              placeholder="Describe your fitness goals..."
            ></textarea>
          </div>
          
          <h2>Change Password</h2>
          <p className="password-info">Leave blank if you don't want to change your password</p>
          
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              minLength="6"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              minLength="6"
            />
          </div>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Profile'}
          </button>
        </form>
      </div>
      
      {/* Goals Management Section */}
      <div className="goals-container">
        <Goals />
      </div>
    </div>
  );
};

export default Profile;
